import { useEffect, useState } from "react";

/**
 * Re-render trigger for theme flips (DSGN-07).
 *
 * Returns a counter that bumps every time the `data-theme` attribute on
 * `<html>` changes — whether from the in-app light/dark toggle or from the
 * ThingsBoard host widget pushing a theme from OUTSIDE React (which is why
 * this watches the DOM via MutationObserver instead of a store flag).
 *
 * Usage: add the returned number to EVERY ECharts option `useMemo`
 * dependency list, so colours resolved through `chartTokens()` /
 * `themeColors` are re-read and the chart repaints when the theme flips
 * (otherwise the option keeps the previous theme's baked-in colours):
 *
 *   const themeVersion = useThemeVersion();
 *   const option = useMemo(() => ({ ... }), [series, themeVersion]);
 *
 * The observer disconnects on unmount.
 */
export function useThemeVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const observer = new MutationObserver(() => setVersion((v) => v + 1));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return version;
}
